import api from '../api';

/**
 * Build a FormData object from a plain payload + optional logo file.
 * This is required because the backend accepts multipart/form-data for logo uploads.
 */
const buildFormData = (payload, logoFile = null) => {
    const fd = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (typeof value === 'object' && !(value instanceof File)) {
            fd.append(key, JSON.stringify(value));
        } else {
            fd.append(key, value);
        }
    });

    if (logoFile) {
        fd.append('logo', logoFile);
    }

    return fd;
};

const minisiteService = {
    // List minisites (with optional params: scope, team_id, status, visibility, search, page)
    list: (params) => api.get('/minisites', { params }),

    // Create a new minisite. Logo is handled via TempUpload:
    // the modal POSTs the file to /api/temp-upload and puts logo_temp_upload_id in payload.
    create: (payload) => api.post('/minisites', payload),

    // Get minisite details
    show: (id) => api.get(`/minisites/${id}`),

    // Update minisite. Same TempUpload pattern for logo.
    update: (id, payload) => api.put(`/minisites/${id}`, payload),

    // Delete minisite
    remove: (id) => api.delete(`/minisites/${id}`),

    // Publish minisite
    publish: (id) => api.post(`/minisites/${id}/publish`),

    // Submit a public minisite form
    submitForm: (slug, data) => api.post(`/public/minisite/${slug}/submit`, { data }),

    // Fetch form submissions
    getFormSubmissions: (id) => api.get(`/minisites/${id}/form-submissions`),

    // Sync pages status
    syncPages: (id, pagesData) => api.put(`/minisites/${id}/pages/status`, { pages: pagesData }),

    // Set page as home
    setHomePage: (id, pageId) => api.put(`/minisites/${id}/pages/${pageId}/home`),

    // Save page rich-text body content to its dedicated content table
    savePageContent: (id, pageId, content) =>
        api.post(`/minisites/${id}/pages/${pageId}/content`, { content }),

    // Save Team specifically with members array directly
    saveTeam: (id, pageId, payload) =>
        api.post(`/minisites/${id}/pages/${pageId}/team`, payload),

    // Delete a specific team member immediately from the database
    deleteTeamMember: (id, pageId, memberId) =>
        api.delete(`/minisites/${id}/pages/${pageId}/team-members/${memberId}`),

    // Save Services/What we do
    saveServices: (id, pageId, payload) =>
        api.post(`/minisites/${id}/pages/${pageId}/what-we-do`, payload),

    // Delete a specific service immediately from the database
    deleteService: (id, pageId, serviceId) =>
        api.delete(`/minisites/${id}/pages/${pageId}/services/${serviceId}`),

    // Save Documents
    saveDocuments: (id, pageId, payload) =>
        api.post(`/minisites/${id}/pages/${pageId}/documents`, payload),

    // Delete a specific document immediately from the database
    deleteDocument: (id, pageId, documentId) =>
        api.delete(`/minisites/${id}/pages/${pageId}/documents/${documentId}`),

    // === Pages endpoints ===

    // Add page to minisite
    addPage: (minisiteId, payload) => api.post(`/minisites/${minisiteId}/pages`, payload),

    // Update page
    updatePage: (minisiteId, pageId, payload) =>
        api.put(`/minisites/${minisiteId}/pages/${pageId}`, payload),

    // Delete page
    deletePage: (minisiteId, pageId) =>
        api.delete(`/minisites/${minisiteId}/pages/${pageId}`),

    // Reorder pages
    reorderPages: (minisiteId, pages) =>
        api.post(`/minisites/${minisiteId}/pages/reorder`, { pages }),

    // === Public endpoint ===

    // Get public minisite by slug
    getPublic: (slug) => api.get(`/public/minisite/${slug}`),
};

export default minisiteService;
